/*
 * The Activity feed's own store (spec 09 §3, roadmap S2-03). It is not part of the client model:
 * the feed is a paged server view, read back per chip and thrown away between visits, while the
 * model is the state a reload has to survive. What the two share is the badge — `activityUnread`
 * lives in the model, because the rail carries it whether or not this page was ever opened — and
 * this store keeps that number honest from every page it loads.
 *
 * Live arrivals come through the client store's event tap. An `activity.item` frame carries the row
 * but neither the message's text nor its link — only the module that owns the message can answer
 * those, and the feed route asks it — so an arrival schedules one bounded refresh of the newest
 * page instead of pushing a half-empty row into the list. `activity.read` needs no refetch: it
 * names the rows that were read, and read is the whole change.
 */

import { createStore, type StoreApi } from "zustand"
import { conversationHref, messageHref, type Addressable } from "../../routes/links.ts"
import { api, messageKeyOf } from "../net/api.ts"
import { conversationAddress } from "../store/conversationName.ts"
import type { Model } from "../store/model.ts"
import { client, type ClientStore } from "../store/store.ts"
import type { ActivityItemView } from "../wire/core.gen.ts"

/** One feed row: the wire row, plus what the owning module said about the message it points at. */
export interface ActivityRow extends ActivityItemView {
	/** The message's first line, absent while no content source is bound or the message is gone. */
	preview?: string
	/** The absolute permalink the owning module builds; the view prefers a path it can route to. */
	link?: string
}

/** One registered kind, as the server's registration table holds it. */
export interface ActivityKindView {
	kind: string
	group: string
	titleKey: string
	/** The sentence its owner wrote for it, where its rows happen in no conversation. */
	sentenceKey?: string
}

/** The chip that shows everything; every other chip is the group of a registered kind. */
export const FILTER_ALL = "all"

/** How many rows one page holds. The server's own ceiling is 50. */
const PAGE_SIZE = 30

/** How long an arrival waits for the ones behind it before the newest page is refetched. */
const REFRESH_DELAY_MS = 250

export interface ActivityFeed {
	filter: string
	items: ActivityRow[]
	/** Every registered kind, as the server sent it; the chips are derived from these. */
	kinds: ActivityKindView[]
	unread: number
	/** The cursor of the next older page, or null when the feed is fully loaded. */
	nextBefore: number | null
	status: "idle" | "loading" | "ready" | "error"
	/** True while an older page is on its way, so the button says so and cannot be pressed twice. */
	loadingMore: boolean
	/** The locale key of the last failure, so the page can say what went wrong. */
	errorKey: string | null
	/** The mark-all that can still be taken back: what it stamped and how much it moved. */
	undo: { readAt: number; count: number } | null
}

function emptyFeed(): ActivityFeed {
	return {
		filter: FILTER_ALL,
		items: [],
		kinds: [],
		unread: 0,
		nextBefore: null,
		status: "idle",
		loadingMore: false,
		errorKey: null,
		undo: null,
	}
}

interface PageResponse {
	items: ActivityRow[]
	nextBefore: number | null
	unread: number
	kinds: ActivityKindView[]
}

interface MutationResponse {
	count: number
	readAt?: number
}

/**
 * Merges a freshly loaded newest page over what is on screen. The rows below the page keep their
 * place — a refresh must not throw away the pages somebody scrolled to — and a row the page carries
 * again replaces the older copy, which is how a read stamp from another tab lands.
 */
export function mergeNewest(current: ActivityRow[], incoming: ActivityRow[]): ActivityRow[] {
	const oldest = incoming.at(-1)
	if (oldest === undefined) return []
	return [...incoming, ...current.filter((row) => row.id < oldest.id)]
}

/** Appends an older page, dropping a row that both pages carry (an item arrived while paging). */
export function appendOlder(current: ActivityRow[], incoming: ActivityRow[]): ActivityRow[] {
	const known = new Set(current.map((row) => row.id))
	return [...current, ...incoming.filter((row) => !known.has(row.id))]
}

/** Stamps rows read in place; rows that are not named, or already read, keep their identity. */
export function withRowsRead(
	items: ActivityRow[],
	ids: readonly number[],
	readAt: number,
): ActivityRow[] {
	const named = new Set(ids)
	let changed = false
	const next = items.map((row) => {
		if (!named.has(row.id) || row.readAt !== undefined) return row
		changed = true
		return { ...row, readAt }
	})
	return changed ? next : items
}

/** One row without its read stamp; the field is dropped rather than set to undefined. */
function withoutReadAt(row: ActivityRow): ActivityRow {
	const rest = { ...row }
	delete rest.readAt
	return rest
}

/** How many rows of a list are still unread — what the page's own counter shows. */
export function unreadRows(items: readonly ActivityRow[]): number {
	return items.filter((row) => row.readAt === undefined).length
}

/**
 * The number the rail, the tab title and the platform badge show: the unread rows of the feed, and
 * nothing else. A direct message is one of those rows since the server writes one for it (UX §6.1),
 * so adding the conversations' unread messages on top would count the same waiting conversation
 * twice — and would count messages where the feed counts things to look at.
 *
 * The consequence is deliberate: marking a row read by hand takes the badge down while the
 * conversation stays bold in the sidebar. Triaging the feed and reading a conversation are two
 * different verbs, and the coupling only ever runs the other way (spec 09 §4).
 */
export function unreadBadge(model: Model): number {
	return model.activityUnread
}

export interface ActivityStoreOptions {
	/** How long an arrival waits before the newest page is refetched; a test sets it to 0. */
	refreshDelayMs?: number
}

/**
 * The feed's behaviour, around a plain Zustand store. It owns the requests, the merge and the
 * subscription to the client's event tap; the page renders `ActivityFeed` and calls these methods.
 */
export class ActivityStore {
	readonly api: StoreApi<ActivityFeed>
	private readonly client: ClientStore
	private readonly refreshDelayMs: number
	private refreshTimer: ReturnType<typeof setTimeout> | undefined
	/** The load whose answer may still be applied; an older one is dropped when it comes back. */
	private generation = 0
	private stopTap: (() => void) | undefined

	constructor(client: ClientStore, options: ActivityStoreOptions = {}) {
		this.client = client
		this.refreshDelayMs = options.refreshDelayMs ?? REFRESH_DELAY_MS
		this.api = createStore<ActivityFeed>(() => emptyFeed())
	}

	get state(): ActivityFeed {
		return this.api.getState()
	}

	/**
	 * Starts listening for the two activity events. It is idempotent, and the returned function
	 * stops it again — a page mounts it and a test tears it down.
	 */
	listen(): () => void {
		this.stopTap ??= this.client.onEvent((event, result) => {
			if (result !== "applied") return
			if (event.type === "activity.item") this.refreshSoon()
			if (event.type === "activity.read") {
				const data = (event as { data: { ids?: number[]; readAt: number } }).data
				if (data.ids && data.ids.length > 0) this.applyRead(data.ids, data.readAt)
				else this.refreshSoon()
			}
		})
		return () => this.stop()
	}

	/** Stops the subscription and forgets a scheduled refresh; the loaded rows stay. */
	stop(): void {
		this.stopTap?.()
		this.stopTap = undefined
		if (this.refreshTimer !== undefined) clearTimeout(this.refreshTimer)
		this.refreshTimer = undefined
	}

	/** Switches the chip and loads its first page. */
	setFilter(filter: string): void {
		if (filter !== this.state.filter) this.api.setState({ filter, items: [], nextBefore: null })
		void this.load()
	}

	/** Loads the newest page of the current chip, keeping what is already below it. */
	async load(): Promise<void> {
		const generation = ++this.generation
		this.api.setState({ status: "loading", errorKey: null })
		try {
			const page = await this.fetchPage()
			if (generation !== this.generation) return
			this.api.setState((feed) => ({
				...feed,
				items: mergeNewest(feed.items, page.items),
				kinds: page.kinds,
				unread: page.unread,
				nextBefore: page.nextBefore,
				status: "ready",
			}))
			this.publishUnread(page.unread)
		} catch (error) {
			if (generation !== this.generation) return
			this.api.setState({ status: "error", errorKey: messageKeyOf(error) })
		}
	}

	/** Loads the page below the one on screen. */
	async loadMore(): Promise<void> {
		const { nextBefore, loadingMore } = this.state
		if (nextBefore === null || loadingMore) return
		this.api.setState({ loadingMore: true, errorKey: null })
		try {
			const page = await this.fetchPage(nextBefore)
			this.api.setState((feed) => ({
				...feed,
				items: appendOlder(feed.items, page.items),
				unread: page.unread,
				nextBefore: page.nextBefore,
				loadingMore: false,
			}))
			this.publishUnread(page.unread)
		} catch (error) {
			this.api.setState({ loadingMore: false, errorKey: messageKeyOf(error) })
		}
	}

	/**
	 * Marks rows read. The list is stamped before the request leaves — triage has to feel immediate
	 * — and a refusal reloads the page rather than guessing what the server now holds.
	 */
	async markRead(ids: readonly number[]): Promise<void> {
		const unstamped = ids.filter((id) => this.rowIsUnread(id))
		if (unstamped.length === 0) return
		this.applyRead(unstamped, Date.now())
		try {
			await api.post<MutationResponse>("/api/activity/read", { ids: unstamped })
		} catch (error) {
			this.api.setState({ errorKey: messageKeyOf(error) })
			await this.load()
		}
	}

	/** Puts rows back into the unread half — the same key that read one takes it back. */
	async markUnread(ids: readonly number[]): Promise<void> {
		const stamped = ids.filter((id) => !this.rowIsUnread(id))
		if (stamped.length === 0) return
		this.api.setState((feed) => ({
			...feed,
			items: feed.items.map((row) => (stamped.includes(row.id) ? withoutReadAt(row) : row)),
			unread: feed.unread + stamped.length,
		}))
		this.publishUnread(this.state.unread)
		try {
			await api.post<MutationResponse>("/api/activity/unread", { ids: stamped })
		} catch (error) {
			this.api.setState({ errorKey: messageKeyOf(error) })
			await this.load()
		}
	}

	/** Marks everything under the current chip read and keeps the stamp, so it can be taken back. */
	async markAllRead(): Promise<void> {
		const filter = this.state.filter
		try {
			const answer = await api.post<MutationResponse>("/api/activity/read", {
				all: true,
				...(filter === FILTER_ALL ? {} : { filter }),
			})
			if (answer.readAt !== undefined && answer.count > 0) {
				this.api.setState({ undo: { readAt: answer.readAt, count: answer.count } })
			}
			await this.load()
		} catch (error) {
			this.api.setState({ errorKey: messageKeyOf(error) })
		}
	}

	/** Takes the last mark-all back: the rows that call stamped become unread again. */
	async undoMarkAll(): Promise<void> {
		const undo = this.state.undo
		if (!undo) return
		this.api.setState({ undo: null })
		try {
			await api.post<MutationResponse>("/api/activity/unread", { readAt: undo.readAt })
		} catch (error) {
			this.api.setState({ errorKey: messageKeyOf(error) })
		}
		await this.load()
	}

	/** Forgets the offer to undo — the window closed, or the person moved on. */
	forgetUndo(): void {
		if (this.state.undo) this.api.setState({ undo: null })
	}

	/** Refetches the newest page once, however many arrivals asked for it in the meantime. */
	private refreshSoon(): void {
		if (this.refreshTimer !== undefined) return
		this.refreshTimer = setTimeout(() => {
			this.refreshTimer = undefined
			void this.load()
		}, this.refreshDelayMs)
	}

	private fetchPage(before?: number): Promise<PageResponse> {
		const params = new URLSearchParams({ limit: String(PAGE_SIZE) })
		if (this.state.filter !== FILTER_ALL) params.set("filter", this.state.filter)
		if (before !== undefined) params.set("before", String(before))
		return api.get<PageResponse>(`/api/activity?${params.toString()}`)
	}

	private rowIsUnread(id: number): boolean {
		return this.state.items.some((row) => row.id === id && row.readAt === undefined)
	}

	/** Stamps rows read on screen and takes the same number off both counters. */
	private applyRead(ids: readonly number[], readAt: number): void {
		this.api.setState((feed) => {
			const items = withRowsRead(feed.items, ids, readAt)
			if (items === feed.items) return feed
			const stamped = unreadRows(feed.items) - unreadRows(items)
			return { ...feed, items, unread: Math.max(0, feed.unread - stamped) }
		})
		this.publishUnread(this.state.unread)
	}

	/** Keeps the model's badge on the number the server just answered with. */
	private publishUnread(unread: number): void {
		this.client.update((model) =>
			model.activityUnread === unread ? model : { ...model, activityUnread: unread },
		)
	}
}

/** Who and where an item happened, resolved against the model the shell already holds. */
export interface ItemContext {
	actorName: string | undefined
	/** The actor's picture, when they have uploaded one: the row draws it instead of their initials. */
	actorAvatar: string | undefined
	conversationName: string | undefined
	/** The in-app path the item opens at, or undefined when nothing can be opened. */
	path: string | undefined
	/** The handle of the group a group mention came through, without the "@". */
	viaGroup: string | undefined
	/**
	 * True when the conversation is a direct one, where naming it beside the actor would read
	 * "Anna Beispiel in @anna": the row says who wrote to you and leaves the place out.
	 */
	direct: boolean
}

/** The slices of the model a row is resolved against; a component subscribes to just these. */
export interface ItemLookup {
	users: Model["users"]
	conversations: Model["conversations"]
	groups: Model["groups"]
	/** The reader, who is never one of the people a direct conversation is named after. */
	me: Model["me"]
}

/**
 * Everything a row needs beyond its own fields, read out of the client model.
 *
 * The path is built from the conversation the model already holds rather than from the row's
 * absolute link: the link is the one a mail carries, and a direct conversation is addressed there
 * by an id this client cannot route to. The link is the fallback for a conversation the model does
 * not know, and a row with neither is shown without being a link.
 */
export function itemContext(lookup: ItemLookup, row: ActivityRow): ItemContext {
	const conv = row.conv
	const meta = conv === undefined ? undefined : lookup.conversations[conv]?.meta
	const actor = row.actorUserId === undefined ? undefined : lookup.users[row.actorUserId]
	return {
		actorName: actor?.displayName,
		actorAvatar: actor?.avatarUrl,
		conversationName: conversationName(lookup, conv),
		path: pathOf(meta, row),
		viaGroup: groupHandle(lookup, row),
		direct: meta !== undefined && DIRECT_KINDS.includes(meta.kind),
	}
}

/** The conversation kinds that are one conversation with one person or a handful of them. */
const DIRECT_KINDS = ["dm", "groupDm"]

/**
 * What a row and the block above it call the conversation: the place, named the way every other
 * surface names it — `#planung` for a channel, the people for a direct conversation (a feed that
 * fell back to "a conversation you are not in" for every direct message would be lying about half
 * its rows).
 *
 * The ladder is `conversationName.ts`, which the chat module's surfaces ask as well: one channel
 * written four ways over five screens is what this delegation ends (X-A3 V21). Only `undefined`
 * stays this feed's own answer — a conversation this client does not hold is drawn as such, and
 * not as an empty place.
 */
export function conversationName(lookup: ItemLookup, conv: number | undefined): string | undefined {
	if (conv === undefined || lookup.conversations[conv] === undefined) return undefined
	return conversationAddress(lookup, conv) || undefined
}

/**
 * Where a row opens. The address of a conversation is the routes layer's to know — a permalink is
 * `/c/<slug>/p/<seq>` for a channel and `/d/<handle>/p/<seq>` for a direct conversation — so this
 * asks rather than building one, and a row whose conversation the model does not hold falls back to
 * the absolute link the server put on it.
 */
function pathOf(meta: Addressable | undefined, row: ActivityRow): string | undefined {
	if (meta === undefined) return samePath(row.link)
	const href = row.seq === undefined ? conversationHref(meta) : messageHref(meta, row.seq)
	return href ?? samePath(row.link)
}

/** How many messages a coalesced row stands for; a row about one single thing carries no count. */
export function itemCount(row: ActivityRow): number | undefined {
	const data = row.data as { count?: unknown } | undefined
	const count = data?.count
	return typeof count === "number" && count > 0 ? count : undefined
}

/** The path of an absolute link of this instance; anything else is not navigated to. */
function samePath(link: string | undefined): string | undefined {
	if (link === undefined || link === "") return undefined
	try {
		const url = new URL(link, globalThis.location.origin)
		return url.origin === globalThis.location.origin ? `${url.pathname}${url.search}` : undefined
	} catch {
		return undefined
	}
}

/**
 * The group a mention came through. The item's `data` belongs to the module that wrote it; the one
 * field core reads by convention is `groupId`, because a group is core's own vocabulary and the
 * model already holds every one of them.
 */
function groupHandle(lookup: ItemLookup, row: ActivityRow): string | undefined {
	const data = row.data as { groupId?: number } | undefined
	if (data?.groupId === undefined) return undefined
	return lookup.groups[data.groupId]?.handle
}

/**
 * The application's feed, over the application's client store. It subscribes to nothing until
 * `listen()` is called, so importing this module costs one object; the Activity page starts and
 * stops it, and a test builds its own pair.
 */
export const activityFeed = new ActivityStore(client)
